import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { posApi } from '@/services/api';

export interface AvailableSerial {
  name: string;
  imei: string;
  mac_address?: string;
  status: string;
}

interface SerialSelectModalProps {
  open: boolean;
  itemName: string;
  itemLabel: string;
  variant?: string;
  excludeImeis?: string[];
  onClose: () => void;
  onSelect: (serial: AvailableSerial) => void;
}

export default function SerialSelectModal({
  open,
  itemName,
  itemLabel,
  variant,
  excludeImeis = [],
  onClose,
  onSelect,
}: SerialSelectModalProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<AvailableSerial[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scan, setScan] = useState('');

  useEffect(() => {
    if (!open || !itemName) return;
    setScan('');
    let cancelled = false;
    const excluded = excludeImeis.join('|');
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await posApi.getItemSerials(itemName, variant, 100);
        const list = (res.data?.message || []) as AvailableSerial[];
        if (!cancelled) {
          const excludeSet = new Set(excluded ? excluded.split('|') : []);
          setRows(list.filter((r) => !excludeSet.has(r.imei)));
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : t('pos.serialLoadFailed'));
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, itemName, variant, excludeImeis.join('|'), t]);

  const filteredRows = useMemo(() => {
    const q = scan.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.imei.toLowerCase().includes(q) ||
        (r.mac_address?.toLowerCase().includes(q) ?? false),
    );
  }, [rows, scan]);

  if (!open) return null;

  const tryScan = () => {
    const code = scan.trim();
    if (!code) return;
    const match = rows.find((r) => r.imei === code);
    if (match) {
      onSelect(match);
      setScan('');
      return;
    }
    setError(t('pos.imeiNotAvailable'));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h3 className="font-semibold text-gray-900">{t('pos.selectImei')}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{itemLabel}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="p-4 border-b border-gray-50">
          <div className="flex gap-2">
            <input
              className="input-field flex-1 font-mono text-sm"
              placeholder={t('pos.scanOrEnterImei')}
              value={scan}
              onChange={(e) => {
                setScan(e.target.value);
                setError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  tryScan();
                }
              }}
              autoFocus
            />
            <button type="button" className="btn-secondary text-sm px-3" onClick={tryScan}>
              {t('pos.use')}
            </button>
          </div>
          <p className="text-xs text-amber-700 mt-2">{t('pos.serialOnlineOnly')}</p>
        </div>
        {error && <div className="px-4 py-2 text-sm text-rose-700 bg-rose-50">{error}</div>}
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <p className="text-sm text-gray-500 p-3">{t('common.loading')}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 p-3">{t('pos.noSerialsAvailable')}</p>
          ) : filteredRows.length === 0 ? (
            <p className="text-sm text-gray-500 p-3">{t('pos.noMatchingSerials')}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filteredRows.map((row) => (
                <li key={row.name}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2.5 hover:bg-primary-50 rounded-lg"
                    onClick={() => onSelect(row)}
                  >
                    <div className="font-mono text-sm font-medium text-gray-900">{row.imei}</div>
                    {row.mac_address ? (
                      <div className="text-xs text-gray-500 mt-0.5">MAC {row.mac_address}</div>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
